import { Injectable, inject } from '@angular/core'
import { GbPaginatedResponse } from '@galabid/gb-dashboard/core/data-access'
import { GbDashboardDialogService } from '@galabid/gb-dashboard/shared/utils'
import { GbNotificationService } from '@galabid/shared/utils/notification'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Update } from '@ngrx/entity'
import { GbEditCategoryDialogComponent } from 'libs/gb-dashboard/item/feature/src/lib/gb-edit-category-dialog/gb-edit-category-dialog.component'
import { GbLoaderService } from '@galabid/shared/utils/loader'
import { GbErrorMessageService } from 'libs/shared/utils/helpers/src/lib/gb-error-message/gb-error-message.service'
import { catchError, exhaustMap, finalize, map, of, switchMap, tap, withLatestFrom } from 'rxjs'
import { CategoriesApiService } from './categories-api.service'
import * as CategoriesActions from './categories.actions'
import { CATEGORIES_LOADER, CategoriesEntity } from './categories.models'
import { GbCreateCategoryDialogComponent } from 'libs/gb-dashboard/item/feature/src/lib/gb-create-category-dialog/gb-create-category-dialog.component'
import { CategoriesFacade } from './categories.facade'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'

@Injectable()
export class CategoriesEffects {
  private actions$ = inject(Actions)

  loadCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.loadCategories),
      withLatestFrom(this.selectedAuctionFacade.urlName$, this.categoriesFacade.allCategories$),
      switchMap(([action, urlName, allCategories]) => {
        const limit = action.limit
        const offset = action.shouldReset ? 0 : allCategories.length
        return this.categoriesApiService.getCategories(urlName, offset, limit, action.searchTerm).pipe(
          map((response: GbPaginatedResponse<CategoriesEntity>) => {
            const props = {
              count: response.count,
              categories: response.results,
            }
            return action.shouldReset
              ? CategoriesActions.setCategories(props)
              : CategoriesActions.loadCategoriesSuccess(props)
          }),
          catchError((error) => {
            return of(CategoriesActions.loadCategoriesFailure({ error }))
          }),
        )
      }),
    ),
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CategoriesActions.loadCategoriesFailure,
          CategoriesActions.deleteCategoryFailure,
          CategoriesActions.createCategoryFailure,
          CategoriesActions.updateCategoriesFailure,
          CategoriesActions.updateCategoryFailure,
        ),
        map((action) => {
          const error = this.gbErrorMessageService.getErrorMessage(action.error.error)
          if (error && typeof error == 'string') {
            this.gbNotificationService.error(error)
          } else {
            this.gbNotificationService.error('GENERIC.ERROR')
          }
        }),
      ),
    { dispatch: false },
  )

  updateCategoriesOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.updateCategoriesOrder),
      tap(() => this.gbLoaderService.showLoaderDialog(CATEGORIES_LOADER.reOrderCategories.id)),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.categoriesApiService
          .updateCategoriesOrder(urlName, {
            categories: action.uuids,
          })
          .pipe(
            map((categories: CategoriesEntity[]) => {
              const updates: Update<CategoriesEntity>[] = []
              categories.forEach((category) => {
                updates.push({
                  id: category.uuid,
                  changes: category,
                })
              })
              return CategoriesActions.updateCategoriesSuccess({
                categories: updates,
              })
            }),
            catchError((error) => {
              return of(CategoriesActions.updateCategoriesFailure({ error }))
            }),
            finalize(() => this.gbLoaderService.hideLoaderDialog(CATEGORIES_LOADER.reOrderCategories.id)),
          ),
      ),
    ),
  )

  updateCategoriesSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CategoriesActions.updateCategoriesSuccess),
        tap(() => {
          this.gbNotificationService.success('ITEMS.CATEGORY.UPDATE_SUCCESS')
        }),
      ),
    { dispatch: false },
  )

  selectCategory$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CategoriesActions.selectCategory),
        map((action) => {
          this.gbDashboardDialogService.openDialog(GbEditCategoryDialogComponent, {
            width: '600px',
          })
        }),
      ),
    { dispatch: false },
  )

  deleteCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.deleteCategory),
      exhaustMap((action) =>
        this.gbDashboardDialogService.filterByConfirmDialog(
          {
            title: 'ITEMS.CATEGORY.CONFIRM_DELETE_TITLE',
            body: 'ITEMS.CATEGORY.CONFIRM_DELETE_BODY',
            confirmButtonText: 'ITEMS.CATEGORY.CONFIRM_DELETE_CONFIRM',
            cancelButtonText: 'ITEMS.CATEGORY.CONFIRM_DELETE_CANCEL',
          },
          action,
        ),
      ),
      tap(() => this.gbLoaderService.showLoaderDialog(CATEGORIES_LOADER.deleteCategory.id)),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.categoriesApiService.deleteCategory(urlName, action.uuid).pipe(
          map(() => CategoriesActions.deleteCategorySuccess({ uuid: action.uuid })),
          catchError((error) => of(CategoriesActions.deleteCategoryFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(CATEGORIES_LOADER.deleteCategory.id)),
        ),
      ),
    ),
  )

  deleteCategorySuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CategoriesActions.deleteCategorySuccess),
        tap(() => this.gbNotificationService.success('ITEMS.CATEGORY.DELETE_SUCCESS')),
      ),
    { dispatch: false },
  )

  openCreateCategoryDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CategoriesActions.openCreateCategoryDialog),
        tap(() =>
          this.gbDashboardDialogService.openDialog(GbCreateCategoryDialogComponent, {
            width: '600px',
          }),
        ),
      ),
    { dispatch: false },
  )

  createCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.createCategory),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([action, urlName]) =>
        this.categoriesApiService.createCategory(urlName, action.label, action.items).pipe(
          map((category) => {
            if (action.onSuccess) action.onSuccess(category)
            return CategoriesActions.createCategorySuccess({ category })
          }),
          catchError((error) => of(CategoriesActions.createCategoryFailure({ error }))),
        ),
      ),
    ),
  )

  createCategorySuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CategoriesActions.createCategorySuccess),
        tap(() => {
          this.gbNotificationService.success('ITEMS.CATEGORY.CREATE_SUCCESS')
        }),
      ),
    { dispatch: false },
  )
  closeCreateDialogOnSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CategoriesActions.createCategorySuccess),
        tap(() => {
          this.gbDashboardDialogService.closeDialog(GbCreateCategoryDialogComponent)
        }),
      ),
    { dispatch: false },
  )

  updateCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.updateCategory),
      withLatestFrom(this.categoriesFacade.selectedCategories$),
      switchMap(([action, selectedCategories]) =>
        this.categoriesApiService
          .updateCategory({
            ...selectedCategories,
            label: action.label,
            label_slug: action.label,
            items: action.items,
          } as CategoriesEntity)
          .pipe(
            map((category) =>
              CategoriesActions.updateCategorySuccess({
                category: { changes: category, id: category.uuid },
              }),
            ),
            catchError((error) => of(CategoriesActions.updateCategoryFailure({ error }))),
          ),
      ),
    ),
  )

  updateCategorySuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CategoriesActions.updateCategorySuccess),
        tap(() => this.gbNotificationService.success('ITEMS.CATEGORY.UPDATE_SUCCESS_SINGLE')),
      ),
    { dispatch: false },
  )

  closeUpdateDialogOnSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CategoriesActions.updateCategorySuccess),
        tap(() => {
          this.gbDashboardDialogService.closeDialog(GbEditCategoryDialogComponent)
        }),
      ),
    { dispatch: false },
  )

  constructor(
    private selectedAuctionFacade: SelectedAuctionFacade,
    private categoriesApiService: CategoriesApiService,
    private gbErrorMessageService: GbErrorMessageService,
    private gbNotificationService: GbNotificationService,
    private gbDashboardDialogService: GbDashboardDialogService,
    private gbLoaderService: GbLoaderService,
    private categoriesFacade: CategoriesFacade,
  ) {}
}
